import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { Scrollbar } from '../scrollbar';
import Lightbox from '../lightbox/Lightbox';
import { useLightBox } from '../lightbox/use-light-box';

import { ChatMessageItem } from './chat-message-item';
import { useMessagesScroll } from './hooks/use-messages-scroll';

// ----------------------------------------------------------------------

ChatMessageList.propTypes = {
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  messages: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  loading: PropTypes.bool
};

export function ChatMessageList({ conversationId, messages = {}, loading }) {
  const normalizedMessages = useMemo(() => {
    if (Array.isArray(messages)) {
      return messages.length > 0 ? { "Aujourd'hui": messages } : {};
    }
    return messages || {};
  }, [messages]);

  const { scrollableRef, messagesEndRef, scrollToBottom } = useMessagesScroll(
    normalizedMessages,
    loading,
    conversationId
  );

  const slides = Object.values(normalizedMessages)
    .flat()
    .filter((message) => message.contentType === 'image')
    .map((message) => ({ src: message.body }));

  const lightbox = useLightBox(slides);

  return (
    <Box
      sx={{ position: 'relative', flex: '1 1 auto', display: 'flex', flexDirection: 'column', height: 1, minHeight: 0 }}
    >
      {loading && (
        <LinearProgress
          color="primary"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 1,
            zIndex: 9
          }}
        />
      )}

      <Scrollbar
        ref={scrollableRef}
        sx={{
          px: 3,
          pt: 5,
          pb: 3,
          flex: '1 1 auto'
        }}
      >
        {Object.entries(normalizedMessages).map(([date, group]) => (
          <div key={date}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              spacing={2}
              divider={<Divider orientation="horizontal" />}
            >
              <Chip label={date} size="small" />
            </Stack>

            {group.map((message, index) => (
              <ChatMessageItem
                key={message.id || `${message.sender_id}-${index}`}
                message={message}
                onOpenLightbox={() => lightbox.onOpen(message.body)}
                onScrollBottom={scrollToBottom}
              />
            ))}
          </div>
        ))}

        {loading && (
          <Box sx={{ mb: 4, mt: 1, display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.lighter', color: 'primary.main' }}>
              <AutoAwesomeIcon fontSize="small" />
            </Avatar>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                py: 1.25,
                px: 2,
                borderRadius: 1.5,
                bgcolor: 'background.neutral'
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mr: 0.5 }}>
                Shahidi Assistant réfléchit
              </Typography>
              <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    animation: 'pulsateDot 1.4s infinite ease-in-out both',
                    '@keyframes pulsateDot': {
                      '0%, 80%, 100%': { transform: 'scale(0.4)', opacity: 0.4 },
                      '40%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    animation: 'pulsateDot 1.4s infinite ease-in-out both',
                    animationDelay: '0.2s',
                    '@keyframes pulsateDot': {
                      '0%, 80%, 100%': { transform: 'scale(0.4)', opacity: 0.4 },
                      '40%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    animation: 'pulsateDot 1.4s infinite ease-in-out both',
                    animationDelay: '0.4s',
                    '@keyframes pulsateDot': {
                      '0%, 80%, 100%': { transform: 'scale(0.4)', opacity: 0.4 },
                      '40%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                />
              </Box>
            </Stack>
          </Box>
        )}

        <div ref={messagesEndRef} style={{ height: 1, minHeight: 1 }} />
      </Scrollbar>

      <Lightbox slides={slides} open={lightbox.open} close={lightbox.onClose} index={lightbox.selected} />
    </Box>
  );
}
